import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { Col, Container, FormControl, Row } from "react-bootstrap";
import Collapsible from "react-collapsible";
import ReactTooltip from "react-tooltip";
import {
	Project,
	ProjectBlock,
	ProjectService,
	ProjectTaskList,
	ProjectTaskService,
} from ".";
import { Greeting, Loading } from "../ui";
import { useUsers } from "../users";
import { ProjectTask, ProjectTaskStatus } from "./ProjectTask";

interface ITasksBtStatus {
	name: string;
	tasks: ProjectTask[];
}

export const MyProjectsListPage = (): ReactElement => {
	const [projects, setProjects] = useState<Project[]>();
	const [completedProjects, setCompletedProjects] = useState<Project[]>();
	const [tasksToStartToday, setTasksToStartToday] = useState<ProjectTask[]>();

	const [tasksByStatus, setTasksByStatus] = useState<ITasksBtStatus[]>([]);

	const [tasksDueSoon, setTasksDueSoon] = useState<ProjectTask[]>();
	const [tasksOverdue, setTasksOverdue] = useState<ProjectTask[]>();
	const [tasksWatching, setTasksWatching] = useState<ProjectTask[]>();

	const [projectSearchTerm, setProjectSearchTerm] = useState();

	const userContext = useUsers();

	const taskService = new ProjectTaskService();
	const projectService = new ProjectService();

	useMemo(() => {
		const statuses = [
			ProjectTaskStatus.InProgress,
			ProjectTaskStatus.InternalReview,
			ProjectTaskStatus.ClientReview,
			ProjectTaskStatus.OnHold,
			ProjectTaskStatus.NotStarted,
		];

		const fetchCompletedProject = async () => {
			const projectData = await projectService.findMineCompleted();
			setCompletedProjects(projectData);
		};

		const fetchTasksToStartToday = async () => {
			const taskData = await taskService.findTasksToStartToday();
			setTasksToStartToday(taskData);
		};

		const fetchTasksDueSoon = async () => {
			const taskData = await taskService.findTasksDueSoonForUser();
			setTasksDueSoon(taskData);
		};

		const fetchTasksOverdue = async () => {
			const taskData = await taskService.findTasksOverdueForUser();
			setTasksOverdue(taskData);
		};

		const fetchTasksByStatus = async (status: string) => {
			const taskData = await taskService.findTasksInStatusForUser(status);

			tasksByStatus.push({
				name: status,
				tasks: taskData,
			});
			setTasksByStatus({ ...tasksByStatus });
		};

		const fetchWatching = async () => {
			const tasksData = await taskService.findTasksWatched();
			setTasksWatching(tasksData);
		};

		statuses.forEach(status => {
			fetchTasksByStatus(status);
		});

		fetchTasksToStartToday();
		fetchTasksDueSoon();
		fetchTasksOverdue();
		fetchWatching();

		fetchCompletedProject();
	}, []);

	useEffect(() => {
		const fetchProjects = async () => {
			const projectData = await projectService.findMineOpen(
				projectSearchTerm,
			);
			setProjects(projectData);
		};

		fetchProjects();
	}, [projectSearchTerm]);

	let openProjectsBlocks = null;
	if (!projects) {
		openProjectsBlocks = <Loading />;
	} else {
		openProjectsBlocks = projects.map((project: Project, index: number) => {
			return <ProjectBlock project={project} key={index} />;
		});
	}

	let completedProjectsBlocks = null;
	if (!completedProjects) {
		completedProjectsBlocks = <Loading />;
	} else {
		completedProjectsBlocks = completedProjects.map(
			(project: Project, index: number) => {
				return <ProjectBlock project={project} key={index} />;
			},
		);
	}

	const iconClose = (
		<FontAwesomeIcon className="float-right" icon={faChevronDown} />
	);
	const iconOpen = (
		<FontAwesomeIcon className="float-right" icon={faChevronUp} />
	);

	const tasksByStatusElements = Object.keys(tasksByStatus).map(
		(_key, index) => {
			const status = tasksByStatus[index];
			return (
				<div key={index}>
					<Collapsible
						trigger={
							<h4 className="section-title">
								Tasks in {status.name} {iconClose}
							</h4>
						}
						triggerWhenOpen={
							<h4 className="section-title">
								Tasks in {status.name} {iconOpen}
							</h4>
						}
						open={
							!status.tasks ||
							(status.tasks &&
								status.tasks.length > 0 &&
								status.name !== ProjectTaskStatus.NotStarted)
						}
					>
						{status.tasks ? (
							<ProjectTaskList
								tasks={status.tasks}
								emptyMessage={`You currently do not have any tasks in ${
									status.name
								}`}
							/>
						) : (
							<Loading />
						)}
					</Collapsible>
				</div>
			);
		},
	);

	return (
		<>
			<Container id="my-projects-page">
				<Row className="mb-5">
					<Col sm={2}>
						<img
							src="/images/project_dashboard_v2.svg"
							style={{ maxWidth: "100%", width: "100%" }}
						/>
					</Col>
					<Col>
						<h3>
							<Greeting />,{" "}
							{userContext.user ? userContext.user.firstName : ""}
							!
						</h3>
						<p>
							This is your project dashboard, all tasks you are
							working, overdue and upcoming will be displayed
							here, along with all projects you are apart of.
						</p>
					</Col>
				</Row>

				<div>
					<Collapsible
						trigger={
							<h4 className="section-title">
								Tasks due soon {iconClose}
							</h4>
						}
						triggerWhenOpen={
							<h4 className="section-title">
								Tasks due soon {iconOpen}
							</h4>
						}
						open={
							!tasksDueSoon ||
							(tasksDueSoon && tasksDueSoon.length > 0)
						}
					>
						{tasksDueSoon ? (
							<ProjectTaskList
								tasks={tasksDueSoon}
								emptyMessage="You currently do not have any tasks due soon."
							/>
						) : (
							<Loading />
						)}
					</Collapsible>
				</div>

				<div>
					<Collapsible
						trigger={
							<h4 className="section-title">
								Tasks to start today {iconClose}
							</h4>
						}
						triggerWhenOpen={
							<h4 className="section-title">
								Tasks to start today {iconOpen}
							</h4>
						}
						open={
							!tasksToStartToday ||
							(tasksToStartToday && tasksToStartToday.length > 0)
						}
					>
						{tasksToStartToday ? (
							<ProjectTaskList
								tasks={tasksToStartToday}
								emptyMessage="You currently do not have any tasks to start today."
							/>
						) : (
							<Loading />
						)}
					</Collapsible>
				</div>

				<div>
					<Collapsible
						trigger={
							<h4 className="section-title">
								Tasks overdue {iconClose}
							</h4>
						}
						triggerWhenOpen={
							<h4 className="section-title">
								Tasks overdue {iconOpen}
							</h4>
						}
						open={
							!tasksOverdue ||
							(tasksOverdue && tasksOverdue.length > 0)
						}
					>
						{tasksOverdue ? (
							<ProjectTaskList
								tasks={tasksOverdue}
								emptyMessage="You currently do not have any tasks overdue."
							/>
						) : (
							<Loading />
						)}
					</Collapsible>
				</div>

				<div>
					<Collapsible
						trigger={
							<h4 className="section-title">
								Tasks watching {iconClose}
							</h4>
						}
						triggerWhenOpen={
							<h4 className="section-title">
								Tasks watching {iconOpen}
							</h4>
						}
						open={false}
					>
						{tasksWatching ? (
							<ProjectTaskList
								tasks={tasksWatching}
								emptyMessage="You currently are not watching any tasks."
							/>
						) : (
							<Loading />
						)}
					</Collapsible>
				</div>

				{tasksByStatusElements}

				<Row noGutters>
					<Col sm={12}>
						<h4 className="section-title">
							Projects
							<FormControl
								placeholder="Filter projects..."
								className="float-right"
								style={{ width: 250 }}
								onChange={(e: any) =>
									setProjectSearchTerm(e.target.value)
								}
							/>
						</h4>
					</Col>
					{openProjectsBlocks}
				</Row>
				<Row noGutters>
					<Col sm={12}>
						<h4 className="section-title">Completed Projects</h4>
					</Col>
					{completedProjectsBlocks}
				</Row>
			</Container>
			<ReactTooltip />
		</>
	);
};
